import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from './api';
import { Immobilisation } from '../models/models';

interface ImmobilisationFilters {
  limit: number;
  offset: number;
}

interface ImmobilisationState {
  immobilisationData: Immobilisation[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedImmobilisation: Immobilisation | null;
  filters: ImmobilisationFilters;
}

const initialState: ImmobilisationState = {
  immobilisationData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  selectedImmobilisation: null,
  filters: { limit: 10, offset: 0 },
};

// Fetch paginé — owner_id et departement_id injectés par l'intercepteur api.ts
export const fetchImmobilisations = createAsyncThunk<
  { data: Immobilisation[]; total: number; totalPages: number; currentPage: number },
  ImmobilisationFilters,
  { rejectValue: string }
>('immobilisation/fetch', async (params, thunkAPI) => {
  try {
    const response = await api.get('/immobilisation', { params });
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Erreur lors du chargement des immobilisations.");
  }
});

// Create ou update (id=-1 → INSERT)
export const createOrUpdateImmobilisation = createAsyncThunk<
  Immobilisation,
  Immobilisation,
  { rejectValue: string }
>('immobilisation/createOrUpdate', async (immo, thunkAPI) => {
  try {
    const response = await api.post('/immobilisation', immo);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Erreur lors de l'enregistrement.");
  }
});

// Delete
export const deleteImmobilisation = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('immobilisation/delete', async (id, thunkAPI) => {
  try {
    await api.delete('/immobilisation', { params: { id } });
    return id;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Erreur lors de la suppression.");
  }
});

const immobilisationSlice = createSlice({
  name: 'immobilisation',
  initialState,
  reducers: {
    setSelectedImmobilisation: (state, action: PayloadAction<Immobilisation | null>) => {
      state.selectedImmobilisation = action.payload;
    },
    clearImmobilisationData: (state) => {
      state.immobilisationData = [];
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    setFilters: (state, action: PayloadAction<Partial<ImmobilisationFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchImmobilisations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchImmobilisations.fulfilled, (state, action) => {
        state.loading = false;
        state.immobilisationData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchImmobilisations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createOrUpdateImmobilisation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createOrUpdateImmobilisation.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Immobilisation enregistrée avec succès.';
      })
      .addCase(createOrUpdateImmobilisation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteImmobilisation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteImmobilisation.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.successMessage = 'Immobilisation supprimée.';
        state.immobilisationData = state.immobilisationData.filter(i => i.id !== action.payload);
      })
      .addCase(deleteImmobilisation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedImmobilisation,
  clearImmobilisationData,
  clearMessages,
  setFilters,
} = immobilisationSlice.actions;

export default immobilisationSlice.reducer;
